import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessagesSquare, Sparkles } from 'lucide-react';
import { inviteApi, THREAD_STATUSES } from '../../lib/inviteApi';
import InviteStatusBadge from '../../components/invite/InviteStatusBadge';

export default function InvitedTab({ exhibitionId, onNavigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (search) params.search = search;
      const d = await inviteApi.listThreads(exhibitionId, params);
      setItems(d.threads || []);
    } catch {
      toast.error('Failed to load threads');
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [exhibitionId, status]);

  const generate = async (threadId) => {
    try {
      await inviteApi.generateOutreach(threadId);
      toast.success('Outreach drafts generated.');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Generate failed'); }
  };

  return (
    <div className="space-y-4" data-testid="invited-tab">
      <div className="flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="invite-select" data-testid="invited-filter-status">
          <option value="">All statuses</option>
          {THREAD_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && refresh()}
          placeholder="Search name / email / org"
          className="invite-input min-w-[220px]"
        />
        <button onClick={refresh} data-clickable="true" className="text-[10px] font-mono px-3 py-1.5 border border-[#333] text-[#aaa] hover:text-white uppercase tracking-[0.14em]">Apply</button>
      </div>

      <div className="border border-[#1a1a1a] overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="text-[#666] uppercase tracking-[0.14em]">
            <tr className="border-b border-[#161616]">
              <th className="text-left p-2.5">Contact</th>
              <th className="text-left p-2.5">Category</th>
              <th className="text-left p-2.5">Email</th>
              <th className="text-left p-2.5">Status</th>
              <th className="text-left p-2.5">Agent</th>
              <th className="text-left p-2.5">Last outbound</th>
              <th className="text-left p-2.5">Last inbound</th>
              <th className="text-left p-2.5">Next FU</th>
              <th className="text-right p-2.5"> </th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-4 text-[#555]">Loading…</td></tr>}
            {!loading && !items.length && <tr><td colSpan={9} className="p-4 text-[#555]">No threads yet.</td></tr>}
            {items.map((row) => {
              const t = row.thread; const c = row.contact || {};
              return (
                <tr key={t.id} className="border-b border-[#0e0e0e] hover:bg-[#0c0c0c]">
                  <td className="p-2.5 text-white">{c.name || '—'}</td>
                  <td className="p-2.5 text-[#aaa]">{c.category || '—'}</td>
                  <td className="p-2.5 text-[#888]">{c.email || '—'}</td>
                  <td className="p-2.5"><InviteStatusBadge status={t.status} /></td>
                  <td className="p-2.5"><InviteStatusBadge status={t.agent_mode} /></td>
                  <td className="p-2.5 text-[#888]">{t.last_outbound_at ? new Date(t.last_outbound_at).toLocaleString() : '—'}</td>
                  <td className="p-2.5 text-[#888]">{t.last_inbound_at ? new Date(t.last_inbound_at).toLocaleString() : '—'}</td>
                  <td className="p-2.5 text-[#888]">{(t.followup_2_due_at || t.followup_1_due_at) ? new Date(t.followup_2_due_at || t.followup_1_due_at).toLocaleDateString() : '—'}</td>
                  <td className="p-2.5 text-right">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button onClick={() => generate(t.id)} data-clickable="true" data-testid={`thread-generate-${t.id}`} className="text-[#aaa] hover:text-[#90E0EF] inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 border border-[#222]">
                        <Sparkles className="w-3 h-3"/> Draft
                      </button>
                      <button onClick={() => onNavigate?.('conversations', { threadId: t.id })} data-clickable="true" data-testid={`thread-open-${t.id}`} className="text-[#aaa] hover:text-[#90E0EF] inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 border border-[#222]">
                        <MessagesSquare className="w-3 h-3"/> Open
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
